import cn from 'classnames'
import { IHRDatasetInfo } from 'legacy/lut/classes/helpers/partner360/HistoricalResultsHelper'
import { useCallback, useMemo } from 'react'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'

import { HIDE_TNET_DISCLAIMER_FOR_COUNTRY } from '../../config'
import { isTnetDisclaimer } from '../../helpers/tnetDisclaimer'

import styles from './CategoryInfo.module.scss'

interface IProps {
  categoryName: string
  partnerCountryCode?: string
  dataSetInfo: IHRDatasetInfo
  isShowTotal?: boolean
  isUnits?: boolean
  isShowValueTypeWarning?: boolean
}

export const CategoryInfo = ({
  dataSetInfo,
  isShowTotal,
  categoryName,
  partnerCountryCode,
  isUnits,
  isShowValueTypeWarning,
}: IProps) => {
  const t = useSafetyTranslation()
  const isHideTnetDisclaimerForCountry = partnerCountryCode === HIDE_TNET_DISCLAIMER_FOR_COUNTRY

  const getInfoType = useCallback((infoType?: string): InfoBlockType => {
    let type = InfoBlockType.Info

    if (infoType) {
      const enumValues = Object.values(InfoBlockType) as string[]
      if (enumValues.includes(infoType)) {
        type = infoType as InfoBlockType
      } else {
        const mapped = (InfoBlockType as any)[infoType]
        if (mapped) {
          type = mapped as InfoBlockType
        }
      }
    }

    return type
  }, [])

  const infoType: InfoBlockType = useMemo<InfoBlockType>(() => {
    return getInfoType(dataSetInfo?.Settings?.InfoType)
  }, [dataSetInfo?.Settings?.InfoType, getInfoType])

  const valueTypeInfoType: InfoBlockType = useMemo<InfoBlockType>(() => {
    return getInfoType(dataSetInfo?.Settings?.ActualsValueTypesInfoType)
  }, [dataSetInfo?.Settings?.ActualsValueTypesInfoType, getInfoType])

  const isDisclaimerAdjustment = useMemo(() => !isUnits && !isShowTotal, [isUnits, isShowTotal])

  const isInfoAdjustment = useMemo(
    () => !isUnits && !isShowTotal && !!dataSetInfo.IsMainHierarchy,
    [isUnits, isShowTotal, dataSetInfo.IsMainHierarchy]
  )

  const isValueTypeInfoAdjustment = useMemo(
    () => !isUnits && !isShowTotal && !!dataSetInfo.IsMainHierarchy,
    [isUnits, isShowTotal, dataSetInfo.IsMainHierarchy]
  )

  const isNotMainHierarchy = useMemo(() => !dataSetInfo.IsMainHierarchy, [dataSetInfo])

  const replaceParams = useMemo(
    () => [{ key: '{category}', value: categoryName || '' }],
    [categoryName]
  )

  const infoKey = useMemo(() => dataSetInfo.Settings?.InfoKey, [dataSetInfo])

  const valueTypeInfoKey = useMemo(
    () => dataSetInfo.Settings?.ActualsValueTypesInfoKey,
    [dataSetInfo]
  )

  const isShowCategoryInfo = useMemo(
    () =>
      !!infoKey && !isUnits && !(isHideTnetDisclaimerForCountry && isTnetDisclaimer(infoKey, t)),
    [infoKey, isHideTnetDisclaimerForCountry, isUnits, t]
  )

  const isShowValueTypeInfo = useMemo(() => {
    return (
      !!isShowValueTypeWarning &&
      !!valueTypeInfoKey &&
      !isUnits &&
      !isTnetDisclaimer(valueTypeInfoKey, t)
    )
  }, [valueTypeInfoKey, isShowValueTypeWarning, t, isUnits])

  if (!dataSetInfo) return <></>

  return (
    <>
      {isNotMainHierarchy && (
        <div
          className={cn(styles.blockWrapper, { [styles.blockAdjustment]: isDisclaimerAdjustment })}
        >
          <InfoBlock
            text={`jbp-planning-hierarchy-disclaimer`}
            replaceParams={replaceParams}
            type={InfoBlockType.Warning}
          />
        </div>
      )}
      {isShowCategoryInfo && (
        <div className={cn(styles.blockWrapper, { [styles.blockAdjustment]: isInfoAdjustment })}>
          <InfoBlock text={infoKey} replaceParams={replaceParams} type={infoType} />
        </div>
      )}
      {isShowValueTypeInfo && (
        <div
          className={cn(styles.blockWrapper, {
            [styles.blockAdjustment]: isValueTypeInfoAdjustment,
          })}
        >
          <InfoBlock
            text={valueTypeInfoKey}
            replaceParams={replaceParams}
            type={valueTypeInfoType}
          />
        </div>
      )}
    </>
  )
}
